import React, { useState, useEffect, useRef } from 'react';
import config from './config';

function CitationNetworkPage({ userUsage }) {
  const [patentInput, setPatentInput] = useState('');
  const [networkData, setNetworkData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [selectedNode, setSelectedNode] = useState(null);
  const svgRef = useRef(null);

  const fetchNetwork = async () => {
    if (!patentInput.trim()) return;
    
    setLoading(true);
    setError('');
    setNetworkData(null);
    
    // Parse patent numbers (comma or space separated)
    const patents = patentInput.split(/[,\s]+/).filter(p => p.trim()).map(p => p.replace(/^US/i, ''));
    
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${config.API_URL}/api/citations/network?token=${token}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ patent_numbers: patents, depth: 1 })
      });
      const data = await response.json();
      
      if (data.error) {
        setError(data.error);
      } else {
        setNetworkData(data);
      }
    } catch (err) {
      setError('Failed to fetch network data');
    }
    
    setLoading(false);
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      fetchNetwork();
    }
  };

  // Simple force-directed layout
  useEffect(() => {
    if (!networkData || !networkData.nodes.length) return;
    
    const width = 900;
    const height = 600;
    const centerX = width / 2;
    const centerY = height / 2;
    
    // Initialize positions
    const nodes = networkData.nodes.map((node, i) => {
      const angle = (2 * Math.PI * i) / networkData.nodes.length;
      const radius = node.type === 'primary' ? 0 : 200;
      return {
        ...node,
        x: centerX + radius * Math.cos(angle) + (Math.random() - 0.5) * 50,
        y: centerY + radius * Math.sin(angle) + (Math.random() - 0.5) * 50,
        vx: 0,
        vy: 0
      };
    });
    
    // Simple force simulation
    const simulate = () => {
      const iterations = 100;
      
      for (let iter = 0; iter < iterations; iter++) {
        // Repulsion between nodes
        for (let i = 0; i < nodes.length; i++) {
          for (let j = i + 1; j < nodes.length; j++) {
            const dx = nodes[j].x - nodes[i].x;
            const dy = nodes[j].y - nodes[i].y;
            const dist = Math.sqrt(dx * dx + dy * dy) || 1;
            const force = 5000 / (dist * dist);
            
            nodes[i].vx -= (dx / dist) * force;
            nodes[i].vy -= (dy / dist) * force;
            nodes[j].vx += (dx / dist) * force;
            nodes[j].vy += (dy / dist) * force;
          }
        }
        
        // Attraction along edges
        networkData.edges.forEach(edge => {
          const source = nodes.find(n => n.id === edge.source);
          const target = nodes.find(n => n.id === edge.target);
          if (source && target) {
            const dx = target.x - source.x;
            const dy = target.y - source.y;
            const dist = Math.sqrt(dx * dx + dy * dy) || 1;
            const force = dist * 0.01;
            
            source.vx += (dx / dist) * force;
            source.vy += (dy / dist) * force;
            target.vx -= (dx / dist) * force;
            target.vy -= (dy / dist) * force;
          }
        });
        
        // Center gravity
        nodes.forEach(node => {
          node.vx += (centerX - node.x) * 0.01;
          node.vy += (centerY - node.y) * 0.01;
        });
        
        // Apply velocities with damping
        nodes.forEach(node => {
          node.x += node.vx * 0.1;
          node.y += node.vy * 0.1;
          node.vx *= 0.9;
          node.vy *= 0.9;
          
          // Keep in bounds
          node.x = Math.max(50, Math.min(width - 50, node.x));
          node.y = Math.max(50, Math.min(height - 50, node.y));
        });
      }
      
      return nodes;
    };
    
    const positionedNodes = simulate();
    setNetworkData(prev => ({ ...prev, positionedNodes }));
    
  }, [networkData?.nodes?.length]);

  const getNodeColor = (type) => {
    switch (type) {
      case 'primary': return '#1976d2';
      case 'citing': return '#4caf50';
      case 'cited': return '#ff9800';
      default: return '#9e9e9e';
    }
  };

  const positionedNodes = networkData?.positionedNodes || [];

  return (
    <div style={{ padding: '24px', maxWidth: '1400px', margin: '0 auto' }}>
      {/* Back Button */}
      <button
        onClick={() => window.location.reload()}
        style={{
          marginBottom: '16px',
          padding: '8px 16px',
          background: '#f5f5f5',
          border: '1px solid #ddd',
          borderRadius: '4px',
          cursor: 'pointer',
          fontSize: '14px'
        }}
      >
        ← Back to Search
      </button>
      {/* Header */}
      <div style={{ marginBottom: '24px' }}>
        <h1 style={{ fontSize: '28px', fontWeight: 'bold', color: '#1a237e', marginBottom: '8px' }}>
          Citation Network Visualization
        </h1>
        <p style={{ color: '#666', fontSize: '14px' }}>
          Visualize patent citation relationships. See how patents connect through citations.
        </p>
        {userUsage && (
          <div style={{ fontSize: '13px', color: '#10b981', marginTop: '12px' }}>
            Premium feature - unlimited access
          </div>
        )}
      </div>

      {/* Search Box */}
      <div style={{ display: 'flex', gap: '12px', marginBottom: '24px' }}>
        <input
          type="text"
          value={patentInput}
          onChange={(e) => setPatentInput(e.target.value)}
          onKeyPress={handleKeyPress}
          placeholder="Enter patent numbers (e.g., 11652247, 11811036 or comma-separated)"
          style={{
            flex: 1,
            padding: '12px 16px',
            fontSize: '16px',
            border: '2px solid #7b1fa2',
            borderRadius: '8px',
            outline: 'none'
          }}
        />
        <button
          onClick={fetchNetwork}
          disabled={loading}
          style={{
            padding: '12px 32px',
            background: '#7b1fa2',
            color: 'white',
            border: 'none',
            borderRadius: '8px',
            fontSize: '16px',
            fontWeight: 'bold',
            cursor: loading ? 'wait' : 'pointer'
          }}
        >
          {loading ? 'Loading...' : 'Visualize'}
        </button>
      </div>

      {/* Legend */}
      <div style={{ display: 'flex', gap: '24px', marginBottom: '16px', fontSize: '14px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <div style={{ width: '16px', height: '16px', borderRadius: '50%', background: '#1976d2' }}></div>
          <span>Primary Patents (your search)</span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <div style={{ width: '16px', height: '16px', borderRadius: '50%', background: '#4caf50' }}></div>
          <span>Citing Patents (cite the primary)</span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <div style={{ width: '16px', height: '16px', borderRadius: '50%', background: '#ff9800' }}></div>
          <span>Cited Patents (cited by primary)</span>
        </div>
      </div>

      {/* Error */}
      {error && (
        <div style={{ padding: '16px', background: '#ffebee', borderRadius: '8px', color: '#c62828', marginBottom: '24px' }}>
          {error}
        </div>
      )}

      {/* Network Stats */}
      {networkData?.stats && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: '12px', marginBottom: '24px' }}>
          <div style={{ background: '#e3f2fd', padding: '16px', borderRadius: '8px', textAlign: 'center' }}>
            <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#1565c0' }}>{networkData.stats.total_nodes}</div>
            <div style={{ fontSize: '12px', color: '#666' }}>Total Patents</div>
          </div>
          <div style={{ background: '#e8f5e9', padding: '16px', borderRadius: '8px', textAlign: 'center' }}>
            <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#2e7d32' }}>{networkData.stats.total_edges}</div>
            <div style={{ fontSize: '12px', color: '#666' }}>Citations</div>
          </div>
          <div style={{ background: '#e3f2fd', padding: '16px', borderRadius: '8px', textAlign: 'center' }}>
            <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#1976d2' }}>{networkData.stats.primary_patents}</div>
            <div style={{ fontSize: '12px', color: '#666' }}>Primary</div>
          </div>
          <div style={{ background: '#e8f5e9', padding: '16px', borderRadius: '8px', textAlign: 'center' }}>
            <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#4caf50' }}>{networkData.stats.citing_patents}</div>
            <div style={{ fontSize: '12px', color: '#666' }}>Citing</div>
          </div>
          <div style={{ background: '#fff3e0', padding: '16px', borderRadius: '8px', textAlign: 'center' }}>
            <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#f57c00' }}>{networkData.stats.cited_patents}</div>
            <div style={{ fontSize: '12px', color: '#666' }}>Cited</div>
          </div>
        </div>
      )}

      {/* Network Visualization */}
      {positionedNodes.length > 0 && (
        <div style={{ background: '#fafafa', borderRadius: '12px', border: '1px solid #ddd', overflow: 'hidden' }}>
          <svg ref={svgRef} width="900" height="600" style={{ display: 'block', margin: '0 auto' }}>
            {/* Edges */}
            {networkData.edges.map((edge, idx) => {
              const source = positionedNodes.find(n => n.id === edge.source);
              const target = positionedNodes.find(n => n.id === edge.target);
              if (!source || !target) return null;
              return (
                <line
                  key={idx}
                  x1={source.x}
                  y1={source.y}
                  x2={target.x}
                  y2={target.y}
                  stroke="#ccc"
                  strokeWidth="1"
                  markerEnd="url(#arrowhead)"
                />
              );
            })}
            
            {/* Arrow marker */}
            <defs>
              <marker id="arrowhead" markerWidth="10" markerHeight="7" refX="20" refY="3.5" orient="auto">
                <polygon points="0 0, 10 3.5, 0 7" fill="#999" />
              </marker>
            </defs>
            
            {/* Nodes */}
            {positionedNodes.map((node, idx) => (
              <g key={idx} style={{ cursor: 'pointer' }} onClick={() => setSelectedNode(node)}>
                <circle
                  cx={node.x}
                  cy={node.y}
                  r={node.type === 'primary' ? 20 : 12}
                  fill={getNodeColor(node.type)}
                  stroke={selectedNode?.id === node.id ? '#000' : '#fff'}
                  strokeWidth={selectedNode?.id === node.id ? 3 : 2}
                />
                <text
                  x={node.x}
                  y={node.y + 30}
                  textAnchor="middle"
                  fontSize="10"
                  fill="#333"
                >
                  {node.patent_number?.slice(-6) || node.id?.slice(-6)}
                </text>
              </g>
            ))}
          </svg>
        </div>
      )}

      {/* Selected Node Info */}
      {selectedNode && (
        <div style={{ marginTop: '24px', padding: '20px', background: '#f3e5f5', borderRadius: '12px', border: '1px solid #ce93d8' }}>
          <h4 style={{ margin: '0 0 12px 0', color: '#7b1fa2' }}>
            Selected Patent: US{selectedNode.patent_number || selectedNode.id}
          </h4>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 2fr', gap: '8px', fontSize: '14px' }}>
            <div><strong>Title:</strong></div>
            <div>{selectedNode.title || 'Unknown'}</div>
            <div><strong>Assignee:</strong></div>
            <div>{selectedNode.assignee || 'Unknown'}</div>
            <div><strong>Type:</strong></div>
            <div style={{ textTransform: 'capitalize' }}>{selectedNode.type}</div>
          </div>
          <div style={{ marginTop: '12px' }}>
            <a 
              href={`https://patents.google.com/patent/US${selectedNode.patent_number || selectedNode.id}`}
              target="_blank"
              rel="noopener noreferrer"
              style={{ color: '#7b1fa2' }}
            >
              View on Google Patents →
            </a>
          </div>
        </div>
      )}

      {/* Empty State */}
      {!networkData && !loading && !error && (
        <div style={{ textAlign: 'center', padding: '60px', color: '#666', background: '#fafafa', borderRadius: '12px' }}>
          <div style={{ fontSize: '48px', marginBottom: '16px' }}>🕸️</div>
          <h3>Visualize Patent Citation Networks</h3>
          <p>Enter one or more patent numbers to see their citation relationships.</p>
          <p style={{ fontSize: '13px', marginTop: '16px' }}>
            Try: 11652247, 11811036, 11584357
          </p>
        </div>
      )}
    </div>
  );
}

export default CitationNetworkPage;